import React, { useState } from 'react';
import { 
  EnvelopeIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  ClockIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { sendTestEmail } from '../../services/emailService';

interface EmailTestResult {
  success: boolean;
  message: string;
  details?: string;
  timestamp: Date;
}

const EmailTestComponent: React.FC = () => {
  const [testEmail, setTestEmail] = useState('');
  const [testResults, setTestResults] = useState<EmailTestResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('application_received');

  const templateOptions = [
    { value: 'application_received', label: 'Application Received Notification' },
    { value: 'application_approved', label: 'Application Approved Notification' },
    { value: 'application_rejected', label: 'Application Rejected Notification' },
  ];

  const addTestResult = (result: Omit<EmailTestResult, 'timestamp'>) => {
    const newResult: EmailTestResult = {
      ...result,
      timestamp: new Date()
    };
    setTestResults(prev => [newResult, ...prev.slice(0, 9)]); // Keep only last 10 results
  };

  const testEmailSend = async () => {
    if (!testEmail.trim()) {
      addTestResult({
        success: false,
        message: 'Email address required',
        details: 'Please enter a valid email address to test'
      });
      return;
    }

    setIsLoading(true);
    try {
      const testData = {
        to_email: testEmail,
        to_name: 'Test User',
        application_id: 'TEST123456',
        applicant_name: 'Test Applicant',
        district: 'Test District',
        submission_date: new Date().toLocaleDateString('en-IN'),
      };

      await sendTestEmail(selectedTemplate, testData);
      
      addTestResult({
        success: true,
        message: `Test email sent successfully to ${testEmail}`,
        details: `Template: ${templateOptions.find(t => t.value === selectedTemplate)?.label}`
      });
    } catch (error) {
      addTestResult({
        success: false,
        message: 'Failed to send test email',
        details: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const checkConfiguration = () => {
    addTestResult({
      success: true,
      message: 'SMTP Configuration Check',
      details: 'Using backend API for email dispatch'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <EnvelopeIcon className="w-6 h-6 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Email Integration Testing</h3>
      </div>

      {/* Configuration Check */}
      <div className="mb-6">
        <button
          onClick={checkConfiguration}
          disabled={isLoading}
          className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          <CogIcon className="w-4 h-4" />
          <span>Check Configuration</span>
        </button>
      </div>



      {/* Email Send Test */}
      <div className="mb-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Test Email Address
          </label>
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Enter email to test"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Template
          </label>
          <select
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          >
            {templateOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={testEmailSend}
          disabled={isLoading || !testEmail.trim()}
          className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
        >
          {isLoading ? <ClockIcon className="w-4 h-4 animate-spin" /> : <EnvelopeIcon className="w-4 h-4" />}
          <span>Send Test Email</span>
        </button>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Test Results</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {testResults.map((result, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border ${
                  result.success 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-start space-x-2">
                  {result.success ? (
                    <CheckCircleIcon className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  ) : (
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${
                      result.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {result.message}
                    </p>
                    {result.details && (
                      <p className={`text-xs mt-1 ${
                        result.success ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {result.details}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {result.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTestComponent;